<template>
  <article>
    <h1 class="text-h3" id="intro" v-intersect="onIntersect">Production Forecasting</h1>
    <p>
      Production Forecasting provides a modern interface to the problem of determining reeserves estimates. It helps the user perform weeks or months of
      painstaking calculations in minutes through an intuitive and responsive interface.
    </p>

    <h2 class="text-h4" id="interface" v-intersect="onIntersect">User Interface</h2>
    <p>The application interface consists of several components that work together to provide the overall functionality.</p>
    <p>
      The interface is divided into two vertical sections; left side shows data available in the current prediction and means to filter and / or group it. It
      consists of a map, filters, and data tables to help narrow the search for perfect the well.
    </p>
    <p>
      The right side consists of all the analytics needed to identify which wells are going to perform well in the future. Here the user is shown summary
      statistics, and charts and plots of various types to help analyse the results.
    </p>

    <h3 class="text-h5" id="toolbar" v-intersect="onIntersect">Toolbar</h3>
    <p>The application toolbar is located at the top and provides access to app-level functions.</p>
    <v-card flat outlined color="grey lighten-2">
      <v-card-title>Available functions</v-card-title>
      <v-data-table :headers="headers" :items="items.appbar" disable-filtering hide-default-footer disable-sort>
        <template v-slot:[`item.icon`]="{ item }">
          <v-icon color="grey">{{ item.icon }}</v-icon>
        </template>
      </v-data-table>
    </v-card>

    <h3 class="text-h5" id="map" v-intersect="onIntersect">Location Map</h3>
    <p>Geographic visualizer and identifier for all the wells in the collection</p>
    <v-card flat outlined color="grey"><v-img eager contain src="@/assets/images/forecast_map.png"></v-img></v-card>
    <v-card flat outlined color="grey lighten-2">
      <v-card-title>Available functions</v-card-title>
      <v-data-table :headers="headers" :items="items.map" disable-filtering hide-default-footer disable-sort>
        <template v-slot:[`item.icon`]="{ item }">
          <v-icon color="grey">{{ item.icon }}</v-icon>
        </template>
      </v-data-table>
    </v-card>

    <h3 class="text-h5" id="filters" v-intersect="onIntersect">Filters</h3>
    <p>
      Filters allow you to limit the scope of work into smaller chunks for easier or comparative analysis. This is especially useful when working with a large
      number of wells (e.g. 10,000) and need to look at specific operators or counties.
    </p>
    <p>
      Filters are available in two distict types; nominal and scalar. Nominal filters can be selected as unique values, while scalars can be used to filter
      within certain numeric ranges. All the filters available in this list are manually selected by the user when generating predictions.
    </p>
    <p>
      Settings allow the analysis to be limited to either a target or a method of prediction. You can limit your focus to only correctly predicted wells, or
      focus on fixing the badly predicted ones.
    </p>
    <p>You can also normalize and scale the outputs by lateral length (if available).</p>
    <v-card flat outlined color="grey"><v-img eager contain src="@/assets/images/forecast_filters.png"></v-img></v-card>
    <p>
      While filtering you can create groups, or selection of wells that fit a specific criteria. Groups created are visible above the filters, and can be
      selected or unselected.
    </p>
    <p>
      When a group is selected, the analysis switches to focus on just the group. Filters aotuomatically load settings from the selected group and indicate the
      composition of the group. When a group is selected, it appears in blue. The number after the title "Fitlers" indicates how many wells are currently
      selected as part of the group. Groups can be deleted by pressing the
      <v-icon>mdi-close-circle</v-icon>
      icon on the group.
    </p>
    <v-card flat outlined color="grey"><v-img eager contain src="@/assets/images/forecast_groups.png"></v-img></v-card>
    <v-card flat outlined color="grey lighten-2">
      <v-card-title>Available functions</v-card-title>
      <v-data-table :headers="headers" :items="items.filters" disable-filtering hide-default-footer disable-sort>
        <template v-slot:[`item.icon`]="{ item }">
          <v-icon color="grey">{{ item.icon }}</v-icon>
        </template>
      </v-data-table>
    </v-card>

    <h3 class="text-h5" id="table" v-intersect="onIntersect">Data Table</h3>
    <p>
      The table underneath filters, show the results of filters in a tabular format. The table can be used to select a single well, and can sorted to view
      certain properties of data associated with each well. Most importantly, the list shows when well started production, how much production has been made
      till date, and what is the expected EUR for the currently selected target.
    </p>
    <v-card flat outlined color="grey"><v-img eager contain src="@/assets/images/forecast_table.png"></v-img></v-card>

    <h3 class="text-h5" id="summary" v-intersect="onIntersect">Summary Analytics</h3>
    <p>Statistical summary of production and recovery of currently selected wells.</p>
    <v-card flat outlined color="grey"><v-img eager contain src="@/assets/images/forecast_summary.png"></v-img></v-card>
    <p>
      When the dimension is added to the analysis, the summary statistics calculates the same aggregates for each unique value of the dimension. This provides a
      quick overview of potential recovery against each dimensional value.
    </p>
    <v-card flat outlined color="grey"><v-img eager contain src="@/assets/images/forecast_dimensions.png"></v-img></v-card>
    <v-card flat outlined color="grey lighten-2">
      <v-card-title>Available functions</v-card-title>
      <v-data-table :headers="headers" :items="items.summary" disable-filtering hide-default-footer disable-sort>
        <template v-slot:[`item.icon`]="{ item }">
          <v-icon color="grey">{{ item.icon }}</v-icon>
        </template>
      </v-data-table>
    </v-card>

    <h3 class="text-h5" id="curves" v-intersect="onIntersect">Aggregates</h3>
    <p>Visual plot of P10, P50, and P90 type curves and aggregates of selected wells</p>
    <v-card flat outlined color="grey"><v-img eager contain src="@/assets/images/forecast_curves.png"></v-img></v-card>
    <v-card flat outlined color="grey lighten-2">
      <v-card-title>Available functions</v-card-title>
      <v-data-table :headers="headers" :items="items.curves" disable-filtering hide-default-footer disable-sort>
        <template v-slot:[`item.icon`]="{ item }">
          <v-icon color="grey">{{ item.icon }}</v-icon>
        </template>
      </v-data-table>
    </v-card>

    <h3 class="text-h5" id="profile" v-intersect="onIntersect">Single Well</h3>
    <p>Visual plot of a single well's productions over its lifetime, including all methods and overlaid with group P50.</p>
    <p>Legend can be clicked to toggle the displayof individual chart elements. Mouseover each element to show underlying data.</p>
    <v-card flat outlined color="grey"><v-img eager contain src="@/assets/images/forecast_profile.png"></v-img></v-card>
    <v-card flat outlined color="grey lighten-2">
      <v-card-title>Available functions</v-card-title>
      <v-data-table :headers="headers" :items="items.profile" disable-filtering hide-default-footer disable-sort>
        <template v-slot:[`item.icon`]="{ item }">
          <v-icon color="grey">{{ item.icon }}</v-icon>
        </template>
      </v-data-table>
    </v-card>

    <h3 class="text-h5" id="probit" v-intersect="onIntersect">Probit Plot</h3>
    <p>Comparative plot of production and percentile ranks of selected wells</p>
    <v-card flat outlined color="grey"><v-img eager contain src="@/assets/images/forecast_probit.png"></v-img></v-card>
    <p>When dimensions are selected in summary mode, probit also updates to reflect the changes. 
      Each dimension is colored differently to help visually separate and identify them. </p>
    <v-card flat outlined color="grey"><v-img eager contain src="@/assets/images/forecast_probit_dimension.png"></v-img></v-card>
    <v-card flat outlined color="grey lighten-2">
      <v-card-title>Available functions</v-card-title>
      <v-data-table :headers="headers" :items="items.probit" disable-filtering hide-default-footer disable-sort>
        <template v-slot:[`item.icon`]="{ item }">
          <v-icon color="grey">{{ item.icon }}</v-icon>
        </template>
      </v-data-table>
    </v-card>
    
    <h3 class="text-h5" id="ratios" v-intersect="onIntersect">Ratios</h3>
    <p>Plot of different types of ratios useful for analysis</p>
    <v-card flat outlined color="grey"><v-img eager contain src="@/assets/images/forecast_ratios.png"></v-img></v-card>

    <h2 class="text-h4" id="glossary" v-intersect="onIntersect">Glossary of Terms</h2>

    <h3 class="text-h5">Actual Production</h3>
    <p>Production volumes reported to production reporting authority.</p>

    <h3 class="text-h5">ARPS</h3>
    <p>Traditional Decline Curve Analysis function.</p>
    <v-img src="@/assets/images/arps.svg" height="auto" width="200" />

    <h3 class="text-h5">Artifical Intelligence</h3>
    <p>A term that reflects the ability of a computer program or machine to think or learn on their own, without being encoded by commands.</p>

    <h3 class="text-h5">Cumulative Actual Production (Single Well)</h3>
    <p>This feature is available when you select a single well.</p>
    <v-img src="@/assets/images/total.svg" height="auto" width="450" />

    <h3 class="text-h5">Cumulative Actual Production (Mean)</h3>
    <p>This feature is available when more than one well has been selected and the Aggregate dropdown is selected as "Mean"</p>
    <v-img src="@/assets/images/mean.svg" height="auto" width="450" />

    <h3 class="text-h5">Cumulative Actual Production (Sum)</h3>
    <p>This feature is available when more than one well has been selected and the Aggregate dropdown is selected as "Sum"</p>
    <v-img src="@/assets/images/sum.svg" height="auto" width="450" />

    <h3 class="text-h5">EUR - Estimated Ultimate Recovery</h3>
    <p>EUR is calculated for each well N and plotted on the central map by color based on the Map colorscale chosen by the user.</p>
    <v-img src="@/assets/images/eur.svg" height="auto" width="100%" />

    <h3 class="text-h5">End of Life</h3>
    <p>
      By default, the Application uses 30 years (600 Months) as the End of Life. Users can change this using the End of Life Slider which appears to the left
      side of the screen.
    </p>

    <h3 class="text-h5">Mean Cumulative Production</h3>
    <v-img src="@/assets/images/mcp.svg" height="auto" width="750" />

    <h3 class="text-h5">Neural Networks (NN)</h3>
    <p>
      Algorithms that can recognize underlying relationships in a set of data through a process that can mimic the way the human brain operates, but with far
      more parameters and volume of data than a human can process. Neural Networks adapt to changing input; so the network generates the best possible result
      without needing to redesign the output criteria.
    </p>

    <h3 class="text-h5">Production Forecast Slider</h3>
    <p>
      Users can choose to focus on a specific range of wells with higher or lower EUR by adjusting the Production Forecast Slider bar which appears to the left
      of the screen.
    </p>

    <h3 class="text-h5">Type Curves</h3>
    <p>
      Users can turn on Type Curves by selecting options using the Toggle Curves dropdown appearing on the left side of the screen. The following curves are
      available:
    </p>
    <ul>
      <li>P90 Value - is a value that has 10% of the range of values less the single value</li>
      <li>P10 Value - is a value that has 90% of the range of values below the single value</li>
      <li>P50 Value - represents an average well</li>
    </ul>
    <p>Users manually pick a group of wells using the lasso map tool and the curves will be automatically generated.</p>

    <h3 class="text-h5">Show wells with short history</h3>
    <p>
      When this feature is turned on wells that have 12 months or less history will appear on the map in a different color. The user can choose the Short
      History Range using the additional slider (from 1 to 12 months). This function allows the user to quickly find younger wells for analysis.
    </p>

    <h3 class="text-h5">Map color feature</h3>
    <p>
      The map can be altered to look at EUR vs Months of production based on user’s preference. So, for example if the goal is to find the highest performing
      wells the user may pick Total Production, whereas if the user is looking to understand how long the wells have been in production they may choose Months
      of Production.
    </p>

    <contents :items="menu" ref="contents" />
  </article>
</template>
<script>
import Contents from "@/components/contents"
export default {
  name: "HelpForecasting",
  components: { Contents },
  data() {
    return {
      menu: [
        { id: "intro", text: "Introduction", depth: 1 },
        { id: "interface", text: "User interface", depth: 1 },
        { id: "toolbar", text: "Toolbar", depth: 2 },
        { id: "map", text: "Location map", depth: 2 },
        { id: "filters", text: "Filters and groups", depth: 2 },
        { id: "table", text: "Data table", depth: 2 },
        { id: "summary", text: "Summary analytics", depth: 2 },
        { id: "curves", text: "Aggregates", depth: 2 },
        { id: "profile", text: "Single Well", depth: 2 },
        { id: "probit", text: "Probit plot", depth: 2 },
        { id: "ratios", text: "Ratios", depth: 2 },
        { id: "glossary", text: "Glossary of terms", depth: 1 },
      ],
      headers: [
        { text: "Icon", value: "icon" },
        { text: "Function", value: "function", cellClass: "text-no-wrap" },
        { text: "Description", value: "description", width: "99%" },
      ],
      items: {
        appbar: [
          {
            icon: "mdi-close-circle",
            function: "Current prediction",
            description:
              "Shows the name of prediction currently loaded in the application. Clicking close will unload the prediction and popup the file open dialog for selection of prediction.",
          },
          {
            icon: "mdi-tune",
            function: "Settings",
            description: "Provides application-level settings, mostly related to economics",
          },
        ],
        map: [
          {
            icon: "mdi-palette-outline",
            function: "Color Scheme",
            description: "Switch between different color schemes to help visualize wells on the map",
          },
          {
            icon: "mdi-function-variant",
            function: "Color Feature",
            description: "Choose what data is used as the feature for the color scheme",
          },
          {
            icon: "mdi-api",
            function: "Select a well",
            description: "Click to select a well by its API number",
          },
          {
            icon: "mdi-cursor-default-outline",
            function: "Click on a well",
            description: "Click to enter into single well selection mode",
          },
          {
            icon: "mdi-arrow-all",
            function: "Move map",
            description: "Click to pan the map around",
          },
          {
            icon: "mdi-select",
            function: "Select wells",
            description: "Click and drag to create a rectangle around the wells for selection",
          },
          {
            icon: "mdi-lasso",
            function: "Select wells",
            description: "Click and drag to create an arbitrary shape around the wells for selection",
          },
          {
            icon: "mdi-plus-box",
            function: "Zoom in",
            description: "Click to zoom into the map one-level at a time",
          },
          {
            icon: "mdi-minus-box",
            function: "Zoom out",
            description: "Click to zoom out of the map one-level at a time",
          },
          {
            icon: "mdi-home",
            function: "Reset view",
            description: "Click to reset zoom level to default",
          },
          {
            icon: "mdi-tag",
            function: "Toggle data display",
            description: "Click to toggle the display of data when hovering over a well with a mouse",
          },
        ],
        filters: [
          {
            icon: "mdi-import",
            function: "Import wells",
            description: "Provide a list of well APIs to be imported into the current workspace as a group",
          },
          {
            icon: "mdi-map-marker-radius-outline",
            function: "Select nearby wells",
            description: "Selects all the wells within specified radius of selected well. Only enabled when in single well mode.",
          },
          {
            icon: "mdi-playlist-plus",
            function: "Create a group",
            description: "Creates a group out of currently selected wells; defaults to all wells.",
          },
          {
            icon: "mdi-content-copy",
            function: "Duplicate a group",
            description: "Duplicates the currently selected group. Only enabled when a group is selected.",
          },
          {
            icon: "mdi-form-textbox",
            function: "Rename a group",
            description: "Shows a dialog box the allows renaming of the currently selected group.",
          },
          {
            icon: "mdi-download",
            function: "Download a group",
            description: "Downloads data of wells in the currently selected group. Columns include what are show in the table below.",
          },
          {
            icon: "mdi-history",
            function: "Save session",
            description:
              "Saves the current session. Currently saved sessions are automatically loaded when user opens the application with the same prediction. Session data includes all groups and current group selection.",
          },
          {
            icon: "mdi-close",
            function: "Clear filters",
            description: "Clears any applied filters",
          },
          {
            icon: "mdi-chevron-up",
            function: "Hide filters",
            description: "Hides all filters to focus on groups and selections.",
          },
        ],
        summary: [
          {
            icon: "mdi-currency-usd",
            function: "Toggle metric",
            description: "Switch between production and revenue",
          },
          {
            icon: "mdi-format-columns",
            function: "Analysis Dimensions",
            description: "Analyse summary statistics through multiple additional dimensions",
          },
        ],
        curves: [
          {
            icon: "mdi-axis-arrow",
            function: "Chart Axis",
            description: "Toggle between linear and logrithmic scale for y-axis",
          },
          {
            icon: "mdi-shuffle",
            function: "Calculation Method",
            description: "Toggle between actual vs cumulative calculations",
          },
          {
            icon: "mdi-magnify",
            function: "Toggle length",
            description: "Expand the plot to end of time; defaults to however long the prediction was declined to",
          },
          {
            icon: "mdi-cog-outline",
            function: "Decline wells",
            description: "Fit an Arps curve through all percentiles",
          },
          {
            icon: "mdi-timer-sand-empty",
            function: "Curve termination",
            description: "Specify curve termination strategy (from months, production, and decline rate)",
          },
          {
            icon: "mdi-pencil-outline",
            function: "Edit Type Curves",
            description: "Open dialog for type curve parameter editing; only available when percentiles are declined",
          },
          {
            icon: "mdi-export",
            function: "Export Type Curves",
            description: "Open dialog for exporting type curves; only available when percentiles are declined",
          },
          {
            icon: "mdi-trash-can-outline",
            function: "Remove Type Curves",
            description: "Remove currently generated type curves; only available when percentiles are declined",
          },
        ],
        profile: [
          {
            icon: "mdi-axis-arrow",
            function: "Chart Axis",
            description: "Toggle between linear and logrithmic scale for y-axis",
          },
          {
            icon: "mdi-magnify",
            function: "Toggle length",
            description: "Expand the plot to end of time; defaults to however long the prediction was declined to",
          },
          {
            icon: "mdi-pencil-outline",
            function: "Edit Well",
            description: "Open dialog for well editing; only edits Arps parameters of currently selected target and method",
          },
          {
            icon: "mdi-plus",
            function: "Add well to group",
            description: "Add well to currently selected group; only available if a group is selected",
          },
          {
            icon: "mdi-minus",
            function: "Remove well from group",
            description: "Remove well from currently selected group; only available if a group is selected",
          },
          {
            icon: "mdi-export",
            function: "Export Well",
            description: "Export data of currently selected well to a CSV file",
          },
          {
            icon: "mdi-close",
            function: "Exit single well mode",
            description: "Exit from the single well mode, and replace chart with aggregates",
          },
        ],
        probit: [
          {
            icon: "mdi-cursor-default-outline",
            function: "Click on a well",
            description: "Click to enter into single well selection mode",
          },
          {
            icon: "mdi-arrow-all",
            function: "Move map",
            description: "Click to pan the map around",
          },
          {
            icon: "mdi-select",
            function: "Select wells",
            description: "Click and drag to create a rectangle around the wells for selection",
          },
          {
            icon: "mdi-lasso",
            function: "Select wells",
            description: "Click and drag to create an arbitrary shape around the wells for selection",
          },
          {
            icon: "mdi-plus-box",
            function: "Zoom in",
            description: "Click to zoom into the map one-level at a time",
          },
          {
            icon: "mdi-minus-box",
            function: "Zoom out",
            description: "Click to zoom out of the map one-level at a time",
          },
          {
            icon: "mdi-home",
            function: "Reset view",
            description: "Click to reset zoom level to default",
          },
          {
            icon: "mdi-tag",
            function: "Toggle data display",
            description: "Click to toggle the display of data when hovering over a well with a mouse",
          },
        ],
      },
    }
  },
}
</script>
